import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Gift, Sparkles } from "@/lib/icons";
import { HandsClapping } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { celebrate } from "@/lib/celebrate";
import { ModalClose } from "@/components/ui/ModalClose";

const MAX_TRIES = 10;

function HighFive({ onDone }) {
  useEffect(() => { celebrate(true); }, []);
  return (
    <div className="text-center py-4" data-testid="reward-high-five">
      <motion.div className="mx-auto w-28 h-28 rounded-full bg-amber-100 grid place-items-center text-amber-800"
        initial={{ scale: 0.4, rotate: -30 }} animate={{ scale: [0.4, 1.25, 1], rotate: [-30, 10, 0] }} transition={{ duration: 0.8, type: "spring", stiffness: 180 }}>
        <motion.div animate={{ rotate: [0, -12, 12, -6, 0] }} transition={{ delay: 0.8, duration: 0.7 }}><HandsClapping weight="duotone" className="w-16 h-16" /></motion.div>
      </motion.div>
      <h3 className="font-display text-3xl font-extrabold text-stone-900 mt-4">High five!</h3>
      <p className="text-sm text-stone-600 mt-2 max-w-xs mx-auto">Ten ideas and none of them fit — that's good information. The win still counts, and so does this hand up.</p>
      <p className="text-xs text-stone-500 mt-2">Tip: add a line or two to your reward profile about what <em>would</em> have landed.</p>
      <button onClick={onDone} data-testid="reward-high-five-done" className="amber-btn text-sm mt-5 px-8">Thanks!</button>
    </div>
  );
}

/** Immediate, habit-tied reward right after a full completion. "No, try another" up to 10 times, then an animated high five. */
export function RewardModal({ habit, onClose, onChanged }) {
  const [reward, setReward] = useState(null);
  const [attempt, setAttempt] = useState(1);
  const [busy, setBusy] = useState(true);
  const declined = useRef([]);

  const generate = async (n) => {
    setBusy(true);
    try {
      const r = await api.post("/rewards/generate", { habit_id: habit.id, attempt: n, exclude: declined.current });
      setReward(r.data);
      onChanged?.();
    } catch { toast.error("Couldn't fetch a reward — you can get one from the Rewards tab."); onClose(); }
    finally { setBusy(false); }
  };
  useEffect(() => { generate(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tryAnother = async () => {
    declined.current = [...declined.current, reward.title];
    await api.post(`/rewards/${reward.id}/reject`).catch(() => {});
    const n = attempt + 1;
    setAttempt(n);
    await generate(n);
  };
  const claim = async () => {
    try {
      const r = await api.post(`/rewards/claim/${reward.id}`);
      toast.success(r.data.message || "Claimed. Enjoy it fully.");
    } catch { toast.error("Couldn't claim just now — it's waiting in your Rewards tab."); }
    onChanged?.();
    onClose();
  };
  const later = () => { onChanged?.(); onClose(); };

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-stone-900/50 backdrop-blur-sm p-4" data-testid="reward-modal" onClick={later}>
      <motion.div className="warm-card relative p-6 w-full max-w-md" initial={{ scale: 0.92, y: 16, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} transition={{ type: "spring", stiffness: 240, damping: 20 }} onClick={(e) => e.stopPropagation()}>
        <ModalClose onClose={later} testid="reward-close" label="Keep it for later" />
        {reward?.kind === "high_five" ? <HighFive onDone={later} /> : (
          <>
            <div className="flex items-center justify-between gap-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800 inline-flex items-center gap-1.5"><Gift className="w-3.5 h-3.5" /> Your reward for “{habit.title}”</div>
              <span className="text-[10px] font-mono text-stone-500" data-testid="reward-attempt">idea {Math.min(attempt, MAX_TRIES)} of {MAX_TRIES}</span>
            </div>
            <AnimatePresence mode="wait">
              {busy || !reward ? (
                <motion.div key="loading" className="py-10 text-center text-sm text-stone-500" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="reward-loading">
                  <Sparkles className="w-6 h-6 mx-auto text-amber-600 animate-pulse" /><div className="mt-2">Picking something just for you…</div>
                </motion.div>
              ) : (
                <motion.div key={reward.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                  {reward.pause ? (
                    <p className="text-sm text-stone-700 mt-4" data-testid="reward-pause">{reward.pause.message}</p>
                  ) : reward.held ? (
                    <p className="text-sm text-stone-700 mt-4" data-testid="reward-held">Your reward is with your coach for a quick look first — it'll appear in your Rewards tab once approved. The points are already yours.</p>
                  ) : (
                    <>
                      <h3 className="font-display text-2xl font-extrabold text-stone-900 mt-3" data-testid="reward-title">{reward.title}</h3>
                      <p className="text-sm text-stone-700 mt-2 leading-relaxed" data-testid="reward-description">{reward.description}</p>
                      {reward.action && <p className="text-sm text-amber-900 mt-2 rounded-2xl bg-amber-50 border border-amber-100 px-3 py-2"><span className="font-semibold">Try: </span>{reward.action}</p>}
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
            <div className="flex gap-2 mt-5">
              {reward && !busy && !reward.pause && !reward.held ? (
                <>
                  <button onClick={tryAnother} data-testid="reward-try-another" className="flex-1 px-4 py-2.5 rounded-full border border-stone-300 text-sm font-semibold text-stone-700 hover:border-amber-600">No, try another</button>
                  <button onClick={claim} data-testid="reward-claim" className="flex-1 amber-btn text-sm">Yes — claim it</button>
                </>
              ) : (
                <button onClick={later} data-testid="reward-later" className="flex-1 px-4 py-2.5 rounded-full border border-stone-300 text-sm font-semibold text-stone-700">{busy ? "Working…" : "Okay"}</button>
              )}
            </div>
            {reward && !busy && !reward.pause && !reward.held && <button onClick={later} data-testid="reward-save-later" className="w-full mt-2 text-xs font-semibold text-stone-500 hover:text-stone-800">Keep it for later (it's in your Rewards tab)</button>}
          </>
        )}
      </motion.div>
    </div>
  );
}

const isToday = (iso) => new Date(iso).toDateString() === new Date().toDateString();

/** Today's rewards strip — sits after habits & check-in, before everything else. */
export function TodayRewards({ rewards, habits, onClaim, onGet, generatingFor }) {
  const today = rewards.filter((r) => isToday(r.created_at) && !r.withdrawn && r.kind !== "high_five");
  const doneNoReward = habits.filter((h) => h.completed_today && !today.some((r) => r.habit_id === h.id));
  if (!today.length && !doneNoReward.length) return null;
  return (
    <section className="warm-card p-5" data-testid="today-rewards">
      <div className="flex items-center gap-2 mb-3"><Gift className="w-5 h-5 text-amber-700" /><h3 className="font-display text-lg font-bold text-stone-900">Today's rewards</h3><span className="text-xs text-stone-500">· one per habit you complete</span></div>
      <div className="grid sm:grid-cols-2 gap-3">
        {today.map((r) => (
          <div key={r.id} className={`rounded-2xl border p-4 ${r.claimed ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50/60"}`} data-testid={`today-reward-${r.id}`}>
            <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{r.habit_title}</div>
            <div className="font-display text-base font-bold text-stone-900 mt-0.5">{r.title}</div>
            <p className="text-xs text-stone-600 mt-1 line-clamp-2">{r.description}</p>
            {r.claimed ? <div className="text-xs font-semibold text-emerald-800 mt-2">Claimed — enjoy it</div>
              : <button onClick={() => onClaim(r.id)} data-testid={`today-reward-claim-${r.id}`} className="mt-2 amber-btn text-xs px-4 py-1.5">Claim it</button>}
          </div>
        ))}
        {doneNoReward.map((h) => (
          <div key={h.id} className="rounded-2xl border border-dashed border-stone-300 p-4 flex items-center justify-between gap-3" data-testid={`today-reward-pending-${h.id}`}>
            <div><div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{h.title}</div><div className="text-sm text-stone-700 mt-0.5">Done today — your reward's waiting.</div></div>
            <button onClick={() => onGet(h)} disabled={generatingFor === h.id} data-testid={`today-reward-get-${h.id}`} className="amber-btn text-xs px-4 py-1.5 shrink-0">{generatingFor === h.id ? "…" : "Get it"}</button>
          </div>
        ))}
      </div>
    </section>
  );
}
